import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// MUI
import Toolbar from '@mui/material/Toolbar';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Card from '@mui/material/Card';

// MUI Icons
import ContactsIcon from '@mui/icons-material/Contacts';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PasswordIcon from '@mui/icons-material/Password';

// Custom Icons
import UserIcon from '@/icons/UserIcon';

// Custom components
import FormContent, { FormContentMode } from '@/components/FormContent';
import { GridContainer, GridItem } from '@/ui/Grid';
import {
  CardActionButtonSave,
  CardHeading,
  CardToolbarButtonBack,
  CardToolbarMenuButton,
  CardToolbarMenuItem,
  CardToolbarMenuItemActivate,
  CardToolbarMenuItemDeactivate,
  CardToolbarMenuItemDelete,
} from '@/ui/Card';
import { FormAvatar, FormHeading, FormHeadingID, FormFillContent, FormTab, FormTabPanel, FormTabs } from '@/ui/Form';
import SnackbarContext, { SnackbarContextType } from '@/context/SnackbarContext/SnackbarContext';
import DialogContext, { DialogContextType } from '@/context/DialogContext/DialogContext';
import DataController from '@/lib/DataController';
import useFormController from '@/components/useFormController';
import ModelTable from '@/ui/Table/ModelTable';
import UserLicenceModel from '@/models/licence';
import { DCRecord } from '@/@types/lib/dataController';
import TenantAdminLicencesDialog from '@/ui/AdminDialogs/TenantAdminLicencesDialog';
import UserPasswordSetDialog from './UserPasswordSetDialog';
import { userService } from '@/services/userService';

interface IUserFormProps {
  dc: DataController;
  recordId: number;
  mode: FormContentMode;
  form: string;
}

const UserForm: FC<IUserFormProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { showNotification } = useContext(SnackbarContext) as SnackbarContextType;
  const { showDialog } = useContext(DialogContext) as DialogContextType;

  const [currentTab, setCurrentTab] = useState(0);
  const [disableAdd, setDisableAdd] = useState(false);
  const [modules, setModules] = useState<DCRecord[]>([]);

  // const close = (result: ClosingDetails) => {
  //    onClose(result);
  // };

  const { dc, recordId, mode, form } = props;
  const { record, validation, fields, onFieldChange, doUpdate, doDelete, doRefresh, doClose, pickerDataControllers, dataChanged } =
    useFormController({
      mode,
      recordId,
      form,
      dc,
      onClose: () => { }
    });

  const [userLicenceRecords, setUserLicenceRecords] = useState<Array<DCRecord>>([]);

  const dcUserLicences = new DataController(UserLicenceModel, `system/tenants/${record.tenant_id}/users/${recordId}/licences`);

  const handleClickBack = () => {
    if (doClose) {
      doClose()
        .then((resp) => {
          if (resp.success) {
            navigate('/admin/users');
          }
        })
        .catch((result) => {
          showNotification(result.error, 'error');
        });
    }
  };

  const handleTabChange = (value: number) => {
    setCurrentTab(value);
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.update_user_success';
            showNotification(msg, 'success');
          }
        })
        .catch((result) => {
          if (result.success === false && result.validationPass === false) {
            // do nothing
          } else {
            const msg = 'error.update_user';
            showNotification(msg, 'error');
          }
        })
        .finally(() => {
          doRefresh();
        });
    }
  };

  const handleDelete = () => {
    if (doDelete) {
      doDelete()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.delete_user_success';
            showNotification(msg, 'success');
            navigate('/admin/users');
          }
        })
        .catch(() => {
          const msg = 'error.delete_user';
          showNotification(msg, 'error');
        });
    }
  };

  const handleActivate = () => {
    if (record.id !== null) {
      try {
        userService.activate(Number(record.id)).then((result) => {
          // @ts-ignore
          if (result.success) {
            const msg = 'messages.activate_user_success';
            showNotification(msg, 'success');
            doRefresh();
          } else {
            const msg = 'error.activate_user';
            showNotification(msg, 'error');
          }
        });
      } catch (error) {
        const msg = 'error.activate_user';
        showNotification(msg, 'error');
      }
    }
  };

  const handleDeactivate = () => {
    if (record.id !== null) {
      try {
        userService.deactivate(Number(record.id)).then((result) => {
          // @ts-ignore
          if (result.success) {
            const msg = 'messages.deactivate_user_success';
            showNotification(msg, 'success');
            // close({ dataChanged: true, action: 'update' });
            navigate(-1);
          } else {
            const msg = 'error.deactiavate_user';
            showNotification(msg, 'error');
          }
        });
      } catch (error) {
        const msg = 'error.deactivate_user';
        showNotification(msg, 'error');
      }
    }
  };

  const handleRowAction = (action: string, id: number, initial_record?: { [key: string]: any }) => {
    if (action === 'edit') {
      showDialog(TenantAdminLicencesDialog, {
        mode: 'update',
        form: 'update_user_licence',
        type: 'system',
        dataId: id,
        onClose: () => { },
        refreshRecords: getLicences,
        initialRecord: initial_record || undefined,
        tenantId: record.tenant_id,
        userId: recordId,
      });
    }
  };

  const title = record.first_name ? (record.first_name as string) : undefined;

  const showSaveButton = currentTab === 0 || currentTab === 1;
  const disableSaveButton = !dataChanged;

  useEffect(() => {
    if (record && record !== null && record.tenant_id !== undefined && record.tenant_id !== null && userLicenceRecords.length === 0) {
      getLicences();
      if (modules.length === 0) {
        dcUserLicences.GetData(`system/tenants/${record.tenant_id}/licences?view=picker`).then((resp) => {
          if (resp.success && resp.data && Array.isArray(resp.data)) {
            setModules(resp.data);
            checkAllUsed();
          }
        });
      }
    }
  }, [record]);

  useEffect(() => {
    checkAllUsed();
  }, [userLicenceRecords]);

  const checkAllUsed = () => {
    if (userLicenceRecords.length === 0) {
      setDisableAdd(false);
      return;
    }
    let foundAll = true;
    modules.forEach((module) => {
      let foundOne = false;
      if (userLicenceRecords.find((licence) => licence.tenant_licence_id === module.id)) {
        foundOne = true;
      }
      if (foundOne === false) foundAll = false;
    });

    setDisableAdd(foundAll);
  };

  const getLicences = () => {
    dcUserLicences
      .GetData()
      .then((resp) => {
        if (resp.success && resp.data && Array.isArray(resp.data) && resp.data.length > 0) {
          setUserLicenceRecords(resp.data);
        } else setUserLicenceRecords([]);
      })
      .catch((resp) => {
        console.log(resp);
      })
      .finally(() => { });
  };

  const handlePasswordSetDialog = () => {
    showDialog(UserPasswordSetDialog, {
      userId: recordId,
      dc,
      mode: 'update',
      form: 'password_set',
      onClose: () => { },
    });
  };

  return (
    <Card>
      <CardHeading>
        <Toolbar variant="dense" disableGutters>
          <FormAvatar ariaLabel="user" icon={<UserIcon />} />
          <FormHeading header={title} subheaderContent={<FormHeadingID id={recordId} />} dataChanged={dataChanged} />
          <FormFillContent />
          <CardToolbarButtonBack onClick={handleClickBack} />
          <CardToolbarMenuButton>
            {record.active ? (
              <CardToolbarMenuItemDeactivate onClick={handleDeactivate} />
            ) : (
              <CardToolbarMenuItemActivate onClick={handleActivate} />
            )}
            {record.user_type_id !== 2 ? <CardToolbarMenuItemDelete onClick={handleDelete} /> : null}
            <CardToolbarMenuItem onClick={handlePasswordSetDialog} label={t('buttons.set_password')} icon={<PasswordIcon />} />
          </CardToolbarMenuButton>
        </Toolbar>
      </CardHeading>
      <CardContent style={{ paddingTop: 0 }}>
        <FormTabs value={currentTab} onChange={handleTabChange} identifier={dc.getSource()}>
          <FormTab id="basic" label={t('core:common.tabs.basic')} icon={<BusinessCenterIcon />} aria-label="basic" value={0} />
          <FormTab id="licences" label={t('core:user.tabs.licences')} icon={<ContactsIcon />} aria-label="licences" value={1} />
        </FormTabs>
        <FormTabPanel value={currentTab} index={0}>
          <GridContainer direction="row" spacing={3}>
            <GridItem>
              <GridContainer direction="row">
                <FormContent
                  title={t('admin:common.sections.basic') as string}
                  fieldNames={[
                    'first_name',
                    'last_name',
                    'username',
                    'email',
                    'status',
                    'user_type_id',
                    'remarks',
                  ]}
                  record={record}
                  validation={validation}
                  onFieldChange={onFieldChange}
                  fields={fields}
                  mode={mode}
                  pickerDataControllers={pickerDataControllers}
                  columns={2}
                />
              </GridContainer>
            </GridItem>
          </GridContainer>
        </FormTabPanel>
        <FormTabPanel value={currentTab} index={1}>
          {record && record !== null && record.tenant_id !== undefined && record.tenant_id !== null ? (
            <ModelTable
              title={t('admin:tenant.sections.licences') as string}
              key="tblid-tenant-admin-user-licences"
              dc={dcUserLicences}
              records={userLicenceRecords}
              allowSelection="one"
              allowRowAction={() => true}
              onRowAction={handleRowAction}
              viewName="system_admin_user"
              allowColumnPicker
              allowFilter
              allowAdd
              addLabel={t('buttons.new_f') as string}
              handleAdd={() => {
                showDialog(TenantAdminLicencesDialog, {
                  mode: 'insert',
                  form: 'insert_user_licence',
                  type: 'system',
                  dataId: undefined,
                  onClose: () => { },
                  refreshRecords: getLicences,
                  initialRecord: undefined,
                  tenantId: record.tenant_id,
                  userId: recordId,
                });
              }}
              disableAdd={disableAdd}
            />
          ) : null}
        </FormTabPanel>
      </CardContent>
      <CardActions>
        <FormFillContent />
        {showSaveButton ? <CardActionButtonSave onClick={handleUpdate} variant="contained" disabled={disableSaveButton} /> : null}
      </CardActions>
    </Card>
  );
}

export default UserForm;
